import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { divIcon } from "leaflet";
import { MapContainer, Marker, TileLayer, useMap } from "react-leaflet";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Car, CheckCircle2, Clock, Store } from "lucide-react";
import { SAR } from "../components/RiyalIcon";
import useRestaurantBrowserIdentity from "../hooks/useRestaurantBrowserIdentity";
import CustomerNotificationPrompt from "../components/CustomerNotificationPrompt";

const STATUS_AR = {
  NEW: "استلمنا طلبك — بانتظار البدء",
  PREPARING: "نجهز طلبك الآن",
  READY: "طلبك جاهز",
  CUSTOMER_ARRIVED: "تم إشعار الموظف — الطلب في طريقه للسيارة",
  OUT_FOR_DELIVERY: "طلبك خرج للتوصيل",
  COMPLETED: "تم استلام الطلب — شكراً لك",
  CANCELLED: "تم إلغاء الطلب",
};

const branchMarkerIcon = divIcon({
  className: "",
  html: `
    <div style="align-items:center;background:#F97316;border:2px solid #fff;border-radius:50%;
      box-shadow:0 1px 4px rgba(0,0,0,.28);display:flex;height:32px;justify-content:center;width:32px">
      <svg aria-hidden="true" fill="none" height="19" stroke="#fff" stroke-linecap="round"
        stroke-linejoin="round" stroke-width="2.2" viewBox="0 0 24 24" width="19">
        <path d="M3 9l1-5h16l1 5" />
        <path d="M5 9v11h14V9" />
        <path d="M9 20v-6h6v6" />
        <path d="M3 9a3 3 0 0 0 6 0 3 3 0 0 0 6 0 3 3 0 0 0 6 0" />
      </svg>
    </div>
  `.trim(),
  iconSize: [36, 36],
  iconAnchor: [18, 18],
});

const customerMarkerIcon = divIcon({
  className: "",
  html: `
    <div style="align-items:center;background:#F97316;border:2px solid #fff;border-radius:50%;
      box-shadow:0 1px 4px rgba(0,0,0,.28);display:flex;height:32px;justify-content:center;width:32px">
      <svg aria-hidden="true" fill="none" height="20" stroke="#fff" stroke-linecap="round"
        stroke-linejoin="round" stroke-width="2.4" viewBox="0 0 24 24" width="20">
        <circle cx="12" cy="8" r="3" />
        <path d="M5 20c.8-3.4 3.1-5 7-5s6.2 1.6 7 5" />
      </svg>
    </div>
  `.trim(),
  iconSize: [36, 36],
  iconAnchor: [18, 18],
});

function FitDeliveryMap({ branchPosition, customerPosition }) {
  const map = useMap();

  useEffect(() => {
    map.fitBounds([branchPosition, customerPosition], {
      padding: [32, 32],
      maxZoom: 15,
    });
  }, [branchPosition, customerPosition, map]);

  return null;
}

export default function OrderConfirmation() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [restaurant, setRestaurant] = useState(null);
  const [arriving, setArriving] = useState(false);

  const load = () => api.get(`/public/orders/${id}`).then(r => setOrder(r.data));
  // The Menu page handles clearing the localStorage pointer on next visit;
  // no need to duplicate that logic here.

  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [id]);

  useEffect(() => {
    if (!order?.restaurant_slug) {
      setRestaurant(null);
      return undefined;
    }

    let active = true;

    api.get(`/public/restaurants/${order.restaurant_slug}`, {
      params: { branch_id: order.branch_id },
    }).then(({ data }) => {
      if (active) {
        setRestaurant(data.restaurant);
      }
    }).catch(() => {
      if (active) {
        setRestaurant(null);
      }
    });

    return () => {
      active = false;
    };
  }, [order?.branch_id, order?.restaurant_slug]);

  useRestaurantBrowserIdentity(restaurant);

  if (!order) return <div className="p-8 text-center text-gray-500">جاري التحميل…</div>;

  const isCar = order.pickup_method === "CAR";
  const isDelivery = order.pickup_method === "DELIVERY";
  const arrived = order.status === "CUSTOMER_ARRIVED";
  const done = order.status === "COMPLETED";
  const assignedBranchName = (
    restaurant?.branch_name
    || order.branch_name
    || restaurant?.name
    || "فرع الاستلام"
  );
  const hasBranchLocation = (
    restaurant?.latitude !== null
    && restaurant?.latitude !== undefined
    && restaurant?.longitude !== null
    && restaurant?.longitude !== undefined
    && Number.isFinite(Number(restaurant.latitude))
    && Number.isFinite(Number(restaurant.longitude))
  );
  const branchMapsUrl = hasBranchLocation
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      `${restaurant.latitude},${restaurant.longitude}`,
    )}`
    : "";
  const branchPosition = [Number(restaurant?.latitude), Number(restaurant?.longitude)];
  const hasCustomerDeliveryLocation = (
    order.delivery_latitude !== null
    && order.delivery_latitude !== undefined
    && order.delivery_longitude !== null
    && order.delivery_longitude !== undefined
    && Number.isFinite(Number(order.delivery_latitude))
    && Number.isFinite(Number(order.delivery_longitude))
  );
  const customerPosition = [
    Number(order.delivery_latitude),
    Number(order.delivery_longitude),
  ];
  const canShowDeliveryMap = hasBranchLocation && hasCustomerDeliveryLocation;

  const clickArrived = async () => {
    setArriving(true);
    try { await api.post(`/public/orders/${id}/arrived`); await load(); toast.success("تم إشعار الموظف"); }
    catch { toast.error("تعذر إرسال الإشعار"); }
    finally { setArriving(false); }
  };

  const openBranchLocation = (event) => {
    event.preventDefault();
    const latitude = restaurant.latitude;
    const longitude = restaurant.longitude;
    const query = encodeURIComponent(`${latitude},${longitude}`);

    if (/Android/i.test(navigator.userAgent)) {
      window.location.href = (
        `intent://maps.google.com/maps?q=${query}`
        + "#Intent;scheme=https;package=com.google.android.apps.maps;"
        + `S.browser_fallback_url=${encodeURIComponent(branchMapsUrl)};end`
      );
      return;
    }

    const isAppleDevice = /iPad|iPhone|iPod/.test(navigator.userAgent)
      || (navigator.userAgent.includes("Macintosh") && navigator.maxTouchPoints > 1);
    if (isAppleDevice) {
      window.location.href = branchMapsUrl;
      return;
    }

    window.location.href = branchMapsUrl;
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2] pb-10">
      <div className="max-w-md mx-auto px-5 py-6">
        <div className="bg-white rounded-3xl card-shadow border border-orange-100 p-6 text-center rise-in">
          <div className="mx-auto w-16 h-16 rounded-full bg-green-100 grid place-items-center">
            <CheckCircle2 className="text-green-600" size={36} />
          </div>
          <h1 className="mt-3 text-2xl font-extrabold" data-testid="order-confirmed">تم استلام طلبك</h1>
          <div className="mt-1 text-gray-600 text-sm">طلب رقم <span className="font-extrabold text-gray-900 num-badge">#{order.order_number}</span></div>
          <div className="mt-4 inline-flex items-center gap-2 pill bg-orange-100 text-orange-800 px-3 py-1.5 text-sm font-bold">
            <Clock size={14}/> {STATUS_AR[order.status]}
          </div>
          <div className="mt-1 text-xs text-gray-500">مدة التجهيز التقديرية: {order.prep_time_minutes} دقيقة</div>
        </div>

        <CustomerNotificationPrompt orderId={order.id} orderStatus={order.status} />

        <div
          className="mt-5 bg-white rounded-2xl border border-orange-100 p-4"
          data-testid="order-location-section"
        >
          {isDelivery ? (
            <>
              <div className="font-bold mb-3">موقع التوصيل</div>
              {canShowDeliveryMap && (
                <div
                  className="h-48 overflow-hidden rounded-xl border border-orange-100"
                  data-testid="delivery-order-location-map"
                >
                    <MapContainer
                      center={branchPosition}
                      zoom={14}
                      className="h-full w-full"
                      dragging={false}
                      scrollWheelZoom={false}
                      doubleClickZoom={false}
                      touchZoom={false}
                      boxZoom={false}
                      keyboard={false}
                      zoomControl={false}
                    >
                      <TileLayer
                        url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                        maxZoom={19}
                        attribution="&copy; OpenStreetMap contributors"
                      />
                      <FitDeliveryMap
                        branchPosition={branchPosition}
                        customerPosition={customerPosition}
                      />
                      <Marker
                        position={branchPosition}
                        icon={branchMarkerIcon}
                        interactive={false}
                        data-testid="delivery-branch-marker"
                      />
                      <Marker
                        position={customerPosition}
                        icon={customerMarkerIcon}
                        interactive={false}
                        data-testid="delivery-customer-marker"
                      />
                    </MapContainer>
                </div>
              )}
              {!canShowDeliveryMap && (
                <div className="text-sm text-gray-700" data-testid="delivery-customer-location">
                  {order.delivery_address || "الموقع المحدد على الخريطة"}
                </div>
              )}
            </>
          ) : (
            <div className="text-sm text-gray-700" data-testid="pickup-branch-location">
              <div className="font-bold text-gray-900">موقع الاستلام</div>
              <div className="mt-2 flex items-center gap-2 font-bold text-gray-900">
                <Store size={18} />
                {assignedBranchName}
              </div>
              {restaurant?.address && (
                <div className="mt-1 text-gray-700" data-testid="pickup-branch-address">
                  {restaurant.address}
                </div>
              )}
              {hasBranchLocation && (
                <a
                  href={branchMapsUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-3 inline-block text-orange-600 font-bold"
                  data-testid="open-branch-google-maps"
                  onClick={openBranchLocation}
                >
                  فتح الموقع في خرائط Google
                </a>
              )}
            </div>
          )}
        </div>

        <div className="mt-5 bg-white rounded-2xl border border-orange-100 p-4">
          <div className="font-bold mb-3">تفاصيل الطلب</div>
          <div className="space-y-1.5 text-sm">
            {order.items.map((it, idx) => (
              <div key={`${it.product_id || it.name}-${idx}`}>
                <div className="flex justify-between">
                  <span>{it.quantity} × {it.name}</span>
                  <span className="num-badge"><SAR>{it.line_total}</SAR></span>
                </div>
                {(it.addons || []).length > 0 && (
                  <div className="text-xs text-gray-500 mt-0.5">
                    {it.addons.map((a, ai) => <div key={`${a.option_id || a.name}-${ai}`}>+ {a.name || a.group_name}</div>)}
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="border-t mt-3 pt-3 flex justify-between text-sm text-gray-600">
            <span>الإجمالي الفرعي</span><span className="num-badge"><SAR>{order.subtotal}</SAR></span>
          </div>
          {order.delivery_fee > 0 && (
            <div className="flex justify-between text-sm text-gray-600">
              <span>رسوم التوصيل</span><span className="num-badge"><SAR>{order.delivery_fee}</SAR></span>
            </div>
          )}
          <div className="flex justify-between mt-1 font-extrabold">
            <span>الإجمالي</span><span className="text-orange-600 num-badge"><SAR>{order.total}</SAR></span>
          </div>
        </div>

        {isCar && (
          <div className="mt-5 bg-white rounded-2xl border border-orange-100 p-4">
            <div className="font-bold mb-2 flex items-center gap-2"><Car size={18}/> بيانات السيارة</div>
            <div className="text-sm text-gray-700">{order.car_type} — {order.car_color} — <span className="num-badge">{order.car_plate}</span></div>
            {!arrived && !done && (
              <>
                <div className="text-xs text-gray-500 mt-3">إذا وصلت للمحل اضغط:</div>
                <button onClick={clickArrived} disabled={arriving}
                  className="mt-2 w-full pill bg-green-600 hover:bg-green-700 text-white font-extrabold text-lg py-4 tap-target disabled:opacity-60"
                  data-testid="arrived-btn">
                  🚗 وصلت
                </button>
              </>
            )}
            {arrived && (
              <div className="mt-3 text-green-700 font-bold text-sm">تم إشعار الموظف، انتظر لحظات وسيصل طلبك إلى السيارة.</div>
            )}
          </div>
        )}

        <div className="mt-6 text-center">
          <Link to={`/r/${order.restaurant_slug || ""}`} data-testid="back-to-menu" className="text-orange-600 font-bold text-sm">العودة للمنيو</Link>
        </div>
      </div>
    </div>
  );
}
